import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { MAX_ARCHIVE_PREVIEW_ENTRY_SIZE } from '../../services/archive.service';

export interface ArchiveManifestEntry {
  path: string;
  uncompressed_size?: number;
  uncompressedSize?: number;
  modified_at?: string;
  modifiedAt?: string;
  is_directory?: boolean;
  isDirectory?: boolean;
}

export interface ArchiveManifest {
  format?: string;
  entries?: ArchiveManifestEntry[];
  total_entries?: number;
  totalEntries?: number;
  total_uncompressed_size?: number;
  totalUncompressedSize?: number;
  truncated?: boolean;
  truncation_reason?: string;
  truncationReason?: string;
  failed?: boolean;
  failure_reason?: string;
  failureReason?: string;
}

interface ArchiveContentsSectionProps {
  manifest?: ArchiveManifest | null;
  /** Path of the currently-previewed entry — highlighted in the tree. */
  selectedEntryPath?: string;
  /** Called when a non-directory, in-range entry is clicked. */
  onEntrySelect?: (path: string) => void;
  /**
   * When false, entries render as non-clickable rows and a small notice
   * explains that click-to-preview isn't available for this archive format.
   * Used for tar / tar.gz (zip is the only format the backend can extract
   * individual entries from in v1).
   */
  interactive?: boolean;
}

// ---------- helpers ----------

function formatSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${Math.round(v * 10) / 10} ${u[i]}`;
}

function getSize(e: ArchiveManifestEntry): number | undefined {
  return e.uncompressed_size ?? e.uncompressedSize;
}

function isDirEntry(e: ArchiveManifestEntry): boolean {
  return !!(e.is_directory ?? e.isDirectory) || e.path.endsWith('/');
}

// ---------- tree model ----------

/**
 * A node in the archive's virtual filesystem tree.
 *
 * `path` matches the form used by the manifest: directories end with a
 * trailing slash, files do not. This lets the parent component address an
 * entry by the exact path the backend expects when extracting it.
 */
interface TreeNode {
  name: string;          // just this segment, e.g. "build.py" or "tools"
  path: string;          // full path matching the manifest convention
  isDirectory: boolean;
  size?: number;         // undefined for directories
  modifiedAt?: string;
  children: Map<string, TreeNode>;
}

/**
 * Build a tree from the flat entry list. Handles three shapes the manifest
 * can deliver:
 *   - Explicit directory entries (path ends with `/`, isDirectory: true)
 *   - File entries with directory components implied by their path
 *   - Mixed: explicit dirs alongside files that live deeper than them
 */
function buildTree(entries: ArchiveManifestEntry[]): TreeNode {
  const root: TreeNode = {
    name: '',
    path: '',
    isDirectory: true,
    children: new Map(),
  };

  for (const entry of entries) {
    const directory = isDirEntry(entry);
    const trimmed = directory && entry.path.endsWith('/')
      ? entry.path.slice(0, -1)
      : entry.path;
    if (!trimmed) continue;

    const segments = trimmed.split('/');
    let cursor = root;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (!segment) continue;
      const isLast = i === segments.length - 1;
      const fullPath = segments.slice(0, i + 1).join('/');

      let child = cursor.children.get(segment);
      if (!child) {
        // Intermediate segments are always directories; only the last segment
        // takes its leaf-ness from the entry itself.
        const childIsDir = !isLast || directory;
        child = {
          name: segment,
          path: childIsDir ? `${fullPath}/` : fullPath,
          isDirectory: childIsDir,
          size: isLast && !directory ? getSize(entry) : undefined,
          modifiedAt: isLast ? (entry.modified_at ?? entry.modifiedAt) : undefined,
          children: new Map(),
        };
        cursor.children.set(segment, child);
      } else if (isLast && !directory) {
        // We had previously created this node as an implicit directory
        // (because a deeper path passed through it), but the manifest now
        // tells us it's a file. Promote it.
        child.isDirectory = false;
        child.path = fullPath;
        child.size = getSize(entry);
        child.modifiedAt = entry.modified_at ?? entry.modifiedAt;
      }

      cursor = child;
    }
  }

  return root;
}

function sortChildren(parent: TreeNode): TreeNode[] {
  // Directories first, then files; both alphabetical, case-insensitive.
  return [...parent.children.values()].sort((a, b) => {
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

/**
 * Collect every ancestor directory path of a leaf entry, so we can auto-expand
 * the tree to reveal a selected entry.
 *
 * Example: "bcc/biampmd/src/foo.cpp" → ["bcc/", "bcc/biampmd/", "bcc/biampmd/src/"]
 */
function ancestorPaths(entryPath: string): string[] {
  const result: string[] = [];
  const segs = entryPath.replace(/\/$/, '').split('/');
  for (let i = 1; i < segs.length; i++) {
    result.push(segs.slice(0, i).join('/') + '/');
  }
  return result;
}

// ---------- component ----------

const INDENT_PX = 16;

export const ArchiveContentsSection: React.FC<ArchiveContentsSectionProps> = ({
  manifest,
  selectedEntryPath,
  onEntrySelect,
  interactive = true,
}) => {
  const entries = manifest?.entries ?? [];
  const tree = useMemo(() => buildTree(entries), [entries]);
  const topLevel = useMemo(() => sortChildren(tree), [tree]);

  // Default expansion: if the archive has a single top-level folder (very
  // common — most zip tools wrap everything in a single directory), open it
  // so users see real contents instead of one empty-looking folder. Otherwise
  // start collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (topLevel.length === 1 && topLevel[0].isDirectory) {
      initial.add(topLevel[0].path);
    }
    return initial;
  });

  // Auto-expand ancestors of the selected entry so the highlighted row is
  // visible in the tree.
  useEffect(() => {
    if (!selectedEntryPath) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const a of ancestorPaths(selectedEntryPath)) next.add(a);
      return next;
    });
  }, [selectedEntryPath]);

  const toggleExpanded = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  if (!manifest) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Archive Contents</Typography>
        <Alert severity="info">
          Archive contents are still being inspected. Refresh in a moment.
        </Alert>
      </Paper>
    );
  }

  if (manifest.failed) {
    const reason = manifest.failure_reason ?? manifest.failureReason;
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Archive Contents</Typography>
        <Alert severity="warning">
          Couldn&apos;t read this archive&apos;s contents
          {reason === 'ENCRYPTED' && ' — the archive is password-protected.'}
          {reason === 'CORRUPT' && ' — the file may be corrupted.'}
          {' '}The archive is still downloadable.
        </Alert>
      </Paper>
    );
  }

  const totalEntries = manifest.total_entries ?? manifest.totalEntries ?? entries.length;
  const totalSize = manifest.total_uncompressed_size ?? manifest.totalUncompressedSize ?? 0;
  const truncationReason = manifest.truncation_reason ?? manifest.truncationReason;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Archive Contents</Typography>

      {manifest.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing partial contents — this archive exceeds inspection limits
          {truncationReason ? ` (${truncationReason})` : ''}.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {totalEntries} entries · {formatSize(totalSize)} total
      </Typography>

      {!interactive && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
        >
          Click-to-preview isn&apos;t supported for this archive format.
        </Typography>
      )}

      <Box
        role="tree"
        sx={{
          maxHeight: 500,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.default',
          py: 0.5,
        }}
      >
        {topLevel.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            (empty archive)
          </Typography>
        ) : (
          topLevel.map((node) => (
            <TreeRow
              key={node.path}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={toggleExpanded}
              selectedEntryPath={selectedEntryPath}
              onEntrySelect={onEntrySelect}
              interactive={interactive}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

// ---------- TreeRow (recursive) ----------

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  selectedEntryPath?: string;
  onEntrySelect?: (path: string) => void;
  interactive: boolean;
}

const TreeRow: React.FC<TreeRowProps> = ({
  node,
  depth,
  expanded,
  onToggle,
  selectedEntryPath,
  onEntrySelect,
  interactive,
}) => {
  if (node.isDirectory) {
    const isExpanded = expanded.has(node.path);
    const childCount = node.children.size;
    return (
      <>
        <Box
          role="treeitem"
          aria-expanded={isExpanded}
          aria-level={depth + 1}
          onClick={() => onToggle(node.path)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: `${depth * INDENT_PX + 4}px`,
            pr: 1,
            py: 0.5,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          {isExpanded
            ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            : <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
          {isExpanded
            ? <FolderOpenIcon fontSize="small" sx={{ ml: 0.25, mr: 1, color: 'warning.main' }} />
            : <FolderIcon fontSize="small" sx={{ ml: 0.25, mr: 1, color: 'warning.main' }} />}
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: 500, minWidth: 0 }}
            noWrap
            title={node.name}
          >
            {node.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 'none', ml: 2 }}
          >
            {childCount} {childCount === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
        {isExpanded && sortChildren(node).map((child) => (
          <TreeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            selectedEntryPath={selectedEntryPath}
            onEntrySelect={onEntrySelect}
            interactive={interactive}
          />
        ))}
      </>
    );
  }

  // File row
  const size = node.size;
  const tooLarge = typeof size === 'number' && size > MAX_ARCHIVE_PREVIEW_ENTRY_SIZE;
  const isSelected = selectedEntryPath === node.path;
  const clickable = interactive && !tooLarge;

  const sizeLabel = tooLarge
    ? `${formatSize(size)} — too large to preview`
    : formatSize(size);

  const row = (
    <Box
      role="treeitem"
      aria-level={depth + 1}
      aria-selected={isSelected || undefined}
      onClick={clickable ? () => onEntrySelect?.(node.path) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: `${depth * INDENT_PX + 4}px`,
        pr: 1,
        py: 0.5,
        cursor: clickable ? 'pointer' : 'default',
        userSelect: 'none',
        opacity: tooLarge ? 0.55 : 1,
        backgroundColor: isSelected ? 'action.selected' : undefined,
        '&:hover': clickable ? { backgroundColor: 'action.hover' } : undefined,
      }}
    >
      {/* spacer where the chevron would sit on a directory row, for alignment */}
      <Box sx={{ width: 20, flex: 'none' }} />
      <FileIcon fontSize="small" sx={{ ml: 0.25, mr: 1, color: 'text.secondary' }} />
      <Typography
        variant="body2"
        sx={{ flex: 1, minWidth: 0 }}
        noWrap
        title={node.path}
      >
        {node.name}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flex: 'none', ml: 2 }}
      >
        {sizeLabel}
      </Typography>
    </Box>
  );

  if (tooLarge) {
    return (
      <Tooltip
        title={`Entries larger than ${formatSize(MAX_ARCHIVE_PREVIEW_ENTRY_SIZE)} can't be previewed inline. Download the archive to view this file.`}
        placement="left"
      >
        {row}
      </Tooltip>
    );
  }
  return row;
};

export default ArchiveContentsSection;
